
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield } from "lucide-react";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export function SecurityTab({ user }: { user: User }) {
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const handleEmailUpdate = async () => {
    if (!newEmail) {
      toast.error("Please enter a new email address");
      return;
    }

    setIsUpdatingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser({ 
        email: newEmail 
      });

      if (error) throw error;
      
      toast.success("Verification email sent to new address. Please check your inbox.");
      setNewEmail("");
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error("Failed to update email. Please try again.");
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handlePasswordReset = async () => {
    setIsResettingPassword(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email!, {
        redirectTo: `${window.location.origin}/auth?recovery=true&type=reset`,
      });

      if (error) throw error;
      
      toast.success("Password reset email sent. Please check your inbox.");
    } catch (error) {
      console.error('Error requesting password reset:', error);
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Email Address
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="New email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="w-full"
          />
          <Button 
            onClick={handleEmailUpdate}
            disabled={isUpdatingEmail}
            className="w-full sm:w-auto whitespace-nowrap"
          >
            <Shield className="mr-2 h-4 w-4" />
            {isUpdatingEmail ? "Updating..." : "Update Email"}
          </Button>
        </div>
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Password
        </label>
        <div className="flex space-x-2">
          <Button 
            onClick={handlePasswordReset}
            disabled={isResettingPassword}
            variant="secondary"
            className="w-full"
          >
            <Shield className="mr-2 h-4 w-4" />
            {isResettingPassword ? "Sending Reset Email..." : "Reset Password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
